/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, FileText, Eye, EyeOff, Star, ExternalLink, ImageOff, Mail, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { MultiImageUploader } from '../../components/MultiImageUploader';
import { BlogImageUploader } from '../../components/BlogImageUploader';
import AIImageGenerateModal from '../../components/admin/AIImageGenerateModal';

import Img from '../../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { resolveImg } from '../../lib/images';

const BlogManagementPage = () => {
  const [posts, setPosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '', excerpt: '', content: '', category: 'news', image_url: '', image_position: 'center', is_published: false, show_in_community: true
  });
  const [aiImageOpen, setAiImageOpen] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`, { withCredentials: true });
      setPosts(response.data);
    } catch (error) {
      // error handled silently
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData, { withCredentials: true });
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/blog`, formData, { withCredentials: true });
        toast.success('Post created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await axios.delete(`${API}/blog/${id}`, { withCredentials: true });
        toast.success('Post deleted successfully');
        fetchPosts();
      } catch (error) {
        toast.error('Failed to delete post');
      }
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      image_url: post.image_url || '',
      image_position: post.image_position || 'center',
      gallery_images: post.gallery_images || [],
      is_published: post.is_published,
      is_featured: !!post.is_featured,
      show_in_community: post.show_in_community !== false,
    });
    setIsDialogOpen(true);
  };

  const quickToggle = async (post, field) => {
    try {
      await axios.patch(`${API}/blog/${post.id}/quick`, { [field]: !post[field] }, { withCredentials: true });
      toast.success(
        field === 'is_published'
          ? (post.is_published ? 'Unpublished' : 'Published')
          : (post.is_featured ? 'Unfeatured' : 'Featured')
      );
      fetchPosts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Quick update failed');
    }
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({ title: '', excerpt: '', content: '', category: 'news', image_url: '', image_position: 'center', gallery_images: [], is_published: false, is_featured: false, show_in_community: true });
  };

  return (
    <div className="p-6 lg:p-8" data-testid="blog-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Blog & News</h1>
          <p className="text-slate-600 mt-1">Create and manage blog posts and news</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#002249] hover:bg-[#0a3570] rounded-sm" data-testid="create-post-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="rounded-sm" data-testid="post-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="post-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">News</SelectItem>
                    <SelectItem value="event_recap">Event Recap</SelectItem>
                    <SelectItem value="recap">Legacy Recap</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                    <SelectItem value="article">Article</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <BlogImageUploader
                value={formData.image_url}
                position={formData.image_position || 'center'}
                onChange={(url, pos) => setFormData((prev) => ({ ...prev, image_url: url, image_position: pos }))}
              />
              <div className="flex justify-end -mt-3">
                <button
                  type="button"
                  onClick={() => setAiImageOpen(true)}
                  className="inline-flex items-center gap-1 text-xs text-[#002249] hover:text-[#E63946] transition-colors font-semibold"
                  data-testid="ai-image-open-blog"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Generate hero image with AI
                </button>
              </div>
              <div className="space-y-2">
                <Label>Excerpt *</Label>
                <Textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} required rows={2} placeholder="Brief summary..." className="rounded-sm" data-testid="post-excerpt-input" />
              </div>
              <div className="space-y-2">
                <Label>Content *</Label>
                <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} required rows={10} className="rounded-sm" data-testid="post-content-input" />
              </div>
              <div className="space-y-2">
                <Label>Image Gallery <span className="text-xs text-slate-500 font-normal">— up to 10 images, shown as a carousel inside the post</span></Label>
                <MultiImageUploader
                  value={formData.gallery_images || []}
                  onChange={(urls) => setFormData({ ...formData, gallery_images: urls })}
                  max={10}
                  label="Add gallery photos"
                  dataTestId="admin-blog-gallery-uploader"
                />
              </div>
              <div className="flex items-center flex-wrap gap-x-6 gap-y-3 pt-2 border-t border-slate-100">
                <div className="flex items-center space-x-2">
                  <Switch checked={formData.is_published} onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })} data-testid="post-publish-switch" />
                  <Label className="cursor-pointer">Publish immediately</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch checked={formData.is_featured} onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })} data-testid="post-feature-switch" />
                  <Label className="cursor-pointer inline-flex items-center gap-1">
                    <Star className={`w-3.5 h-3.5 ${formData.is_featured ? 'fill-amber-500 text-amber-500' : 'text-slate-400'}`} />
                    Feature on homepage
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch checked={formData.show_in_community !== false} onCheckedChange={(checked) => setFormData({ ...formData, show_in_community: checked })} data-testid="post-community-switch" />
                  <Label className="cursor-pointer">Show in Community Feed</Label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="rounded-sm">Cancel</Button>
                <Button type="submit" className="bg-[#002249] hover:bg-[#0a3570] rounded-sm" data-testid="post-submit-btn">{editingPost ? 'Update' : 'Create'} Post</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Posts List */}
      <div className="bg-white border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold text-[#002249]">Title</th>
                <th className="text-left p-4 font-semibold text-[#002249] hidden md:table-cell">Category</th>
                <th className="text-left p-4 font-semibold text-[#002249] hidden lg:table-cell">Author</th>
                <th className="text-left p-4 font-semibold text-[#002249]">Status</th>
                <th className="text-right p-4 font-semibold text-[#002249]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {posts.map((post, index) => {
                const imgSrc = resolveImg(post.image_url || post.featured_image);
                return (
                <tr key={post.id} className="hover:bg-slate-50" data-testid={`post-row-${index}`}>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-14 h-14 bg-slate-100 rounded-md flex-shrink-0 overflow-hidden ring-1 ring-slate-200" data-testid={`post-thumb-${index}`}>
                        {imgSrc ? (
                          <Img variant="generic"
                            src={imgSrc}
                            alt=""
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.classList.add('flex','items-center','justify-center'); e.currentTarget.parentElement.innerHTML = '<div class="text-slate-300"><svg xmlns=http://www.w3.org/2000/svg width=20 height=20 viewBox=0 0 24 24 fill=none stroke=currentColor stroke-width=2 stroke-linecap=round stroke-linejoin=round><path d=M21 9v6M3 9v6m18-3H3m3-6h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z/></svg></div>'; }}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <ImageOff className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-[#002249] truncate max-w-[320px]">{post.title}</p>
                          {post.is_featured && (
                            <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5" data-testid={`featured-badge-${index}`}><Star className="w-2.5 h-2.5 fill-current" />FEATURED</span>
                          )}
                          {post.source_event_id && (
                            <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" data-testid={`event-recap-badge-${index}`}>EVENT RECAP</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1">{post.excerpt}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className="bg-slate-100 text-slate-700 text-xs font-medium px-2 py-1 rounded-sm capitalize">{post.category}</span>
                  </td>
                  <td className="p-4 hidden lg:table-cell text-sm text-slate-600">{post.author_name || 'Unknown'}</td>
                  <td className="p-4">
                    <button
                      type="button"
                      onClick={() => quickToggle(post, 'is_published')}
                      className={`inline-flex items-center text-xs font-semibold px-2 py-1 rounded transition-colors ${post.is_published ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`toggle-publish-${index}`}
                      title={post.is_published ? 'Click to unpublish' : 'Click to publish'}
                    >
                      {post.is_published ? <><Eye className="w-3 h-3 mr-1" />Published</> : <><EyeOff className="w-3 h-3 mr-1" />Draft</>}
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end items-center space-x-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => quickToggle(post, 'is_featured')}
                        className={`${post.is_featured ? 'text-amber-600 hover:text-amber-700' : 'text-slate-400 hover:text-amber-600'}`}
                        data-testid={`feature-post-${index}`}
                        title={post.is_featured ? 'Unfeature' : 'Feature on homepage'}
                      >
                        <Star className={`w-4 h-4 ${post.is_featured ? 'fill-current' : ''}`} />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => quickToggle(post, 'include_in_newsletter')}
                        className={`${post.include_in_newsletter ? 'text-[#E63946] hover:text-[#c02a35]' : 'text-slate-400 hover:text-[#E63946]'}`}
                        data-testid={`newsletter-toggle-blog-${index}`}
                        title={post.include_in_newsletter ? 'In newsletter pipeline — click to remove' : 'Add to newsletter pipeline'}
                      >
                        <Mail className={`w-4 h-4 ${post.include_in_newsletter ? 'fill-[#E63946]/15' : ''}`} />
                      </Button>
                      <Button variant="ghost" size="sm" asChild data-testid={`preview-post-${index}`} title="Preview live">
                        <Link to={`/news/${post.id}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(post)} data-testid={`edit-post-${index}`} title="Edit">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-post-${index}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {posts.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p>No posts yet. Create your first post!</p>
            </div>
          )}
        </div>
      </div>

      {aiImageOpen && (
        <AIImageGenerateModal
          contextType="blog"
          contextId={editingPost?.id || null}
          contextData={{
            title: formData.title,
            excerpt: formData.excerpt,
            category: formData.category,
          }}
          contextLabel={formData.title || 'New article'}
          existingImageUrl={formData.image_url || null}
          onImageSelected={(url) => {
            setFormData((prev) => ({ ...prev, image_url: url }));
            toast.success('AI image attached to article');
          }}
          onClose={() => setAiImageOpen(false)}
        />
      )}
    </div>
  );
};

export default BlogManagementPage;
